import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Dialog } from 'primereact/dialog';
import { FiRefreshCw, FiSearch, FiThumbsUp, FiThumbsDown, FiEye, FiMessageSquare, FiClock } from 'react-icons/fi';
import { handleUnauthorized } from '../../utils/apiClient';
import DataQualityScanResult from './DataQualityScanResult';
import '../commissionscan/CommissionScan.css';
import '../../styles/QuoteProposal.css';
import '../../styles/Voice.css';
import '../../styles/DataQualityScanHistory.css';
import { useToast } from '../../context/ToastContext';
const PAGE_SIZE       = 10;
const getToolboxToken = (): string => localStorage.getItem('toolboxToken') || '';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  Started:      { label: 'Started',      color: '#fbbf24' },
  Inprogress:   { label: 'In Progress',  color: '#a78bfa' },
  Success:      { label: 'Success',      color: '#34d399' },
  Failed:       { label: 'Failed',       color: '#f87171' },
  NotSupported: { label: 'Not Supported', color: '#9ca3af' },
};

const formatDate = (d: string) => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    return `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${dt.getFullYear()} ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
  } catch { return d; }
};

interface HistoryRow {
  transactionId:       string;
  fileName:            string;
  userName:            string;
  status:              string;
  created:             string;
  isUpdateInAMS:       boolean;
  matchesResponseJson: string;
  totalCount:          number;
  isLiked?:            boolean | null;
  isCheckingStatus?:   boolean;
}

const DataQualityScanHistory: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [rows,        setRows]        = useState<HistoryRow[]>([]);
  const [loading,     setLoading]     = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [page,        setPage]        = useState(1);
  const [totalCount,  setTotalCount]  = useState(0);

  const [previewOpen,       setPreviewOpen]       = useState(false);
  const [previewData,       setPreviewData]       = useState<any[]>([]);
  const [previewTxId,       setPreviewTxId]       = useState('');
  const [previewUpdateFlag, setPreviewUpdateFlag] = useState(false);

  /* Feedback */
  const [feedbackTxId,       setFeedbackTxId]       = useState<string | null>(null);
  const [feedbackText,       setFeedbackText]       = useState('');
  const [feedbackRating,     setFeedbackRating]     = useState<boolean | null>(null);
  const [feedbackAnchor,     setFeedbackAnchor]     = useState<{ top: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedBackError,      setFeedBackError]      = useState('');

  const feedbackRef    = useRef<HTMLDivElement>(null);
  const autoRefreshRef = useRef<any>(null);

  /* Pagination */
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageList = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce<(number | '…')[]>((acc, p, idx, arr) => {
      if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);

  const fetchHistory = useCallback((p = 1) => {
    setLoading(true);
    axios.get(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/DataQuality/history?pageNumber=${p}&recordCount=${PAGE_SIZE}`, {
      headers: { Authorization: `Bearer ${getToolboxToken()}` },
    })
      .then(res => {
        setRows(res.data || []);
        setTotalCount(res.data?.[0]?.totalCount ?? 0);
        setLoading(false);
      })
      .catch((err: any) => {
        setLoading(false);
        if (err.response?.status === 401) handleUnauthorized();
      });
  }, []);

  useEffect(() => { fetchHistory(page); }, [page, fetchHistory]);

  useEffect(() => { setPage(1); }, [globalFilter]);

  /* Auto-refresh while first row is processing */
  useEffect(() => {
    const first = rows[0];
    const isProcessing = first?.status === 'Started' || first?.status === 'Inprogress';
    if (isProcessing) {
      autoRefreshRef.current = setInterval(() => fetchHistory(page), 10000);
    } else {
      clearInterval(autoRefreshRef.current);
    }
    return () => clearInterval(autoRefreshRef.current);
  }, [rows, page, fetchHistory]);

  const checkStatus = (row: HistoryRow) => {
    setRows(prev => prev.map(r => r.transactionId === row.transactionId ? { ...r, isCheckingStatus: true } : r));
    axios.get(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/DataQuality/checkstatus/${row.transactionId}`, {
      headers: { Authorization: `Bearer ${getToolboxToken()}` },
    })
      .then(res => {
        setRows(prev => prev.map(r => r.transactionId === row.transactionId ? { ...r, isCheckingStatus: false } : r));
        if (res.data?.success && ['Success', 'Failed', 'NotSupported'].includes(res.data.status)) {
          fetchHistory(page);
        }
      })
      .catch((err: any) => {
        setRows(prev => prev.map(r => r.transactionId === row.transactionId ? { ...r, isCheckingStatus: false } : r));
        if (err.response?.status === 401) handleUnauthorized();
      });
  };

  const openPreview = (row: HistoryRow) => {
    try {
      setPreviewData(JSON.parse(row.matchesResponseJson));
    } catch { setPreviewData([]); }
    setPreviewTxId(row.transactionId);
    setPreviewUpdateFlag(row.isUpdateInAMS);
    setPreviewOpen(true);
  };

  /* ── Feedback ── */
  const openFeedback = useCallback((row: HistoryRow, e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackTxId(row.transactionId);
    setFeedbackText('');
    setFeedbackRating(row.isLiked ?? null);
    setFeedBackError('');
    setFeedbackAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedbackTxId(null);
    setFeedbackAnchor(null);
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedBackError('');
  }, []);

  const onThumbsUpClick = useCallback(() => {
    if (!feedbackTxId) return;
    const cur = rows.find(r => r.transactionId === feedbackTxId);
    const next: boolean | null = cur?.isLiked === true ? null : true;
    setRows(prev => prev.map(r => r.transactionId === feedbackTxId ? { ...r, isLiked: next } : r));
    closeFeedback();
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAICommissionStatement/feedback`,
      { Target: 4, TransactionId: feedbackTxId, IsLiked: next, Feedback: feedbackText ?? '' },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    ).catch((err: any) => { if (err.response?.status === 401) handleUnauthorized(); });
  }, [feedbackTxId, rows, feedbackText, closeFeedback]);

  const onThumbsDownClick = useCallback(() => {
    if (!feedbackTxId) return;
    const cur = rows.find(r => r.transactionId === feedbackTxId);
    const next: boolean | null = cur?.isLiked === false ? null : false;
    setRows(prev => prev.map(r => r.transactionId === feedbackTxId ? { ...r, isLiked: next } : r));
    closeFeedback();
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAICommissionStatement/feedback`,
      { Target: 4, TransactionId: feedbackTxId, IsLiked: next, Feedback: feedbackText ?? '' },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    ).catch((err: any) => { if (err.response?.status === 401) handleUnauthorized(); });
  }, [feedbackTxId, rows, feedbackText, closeFeedback]);

  const onSubmitFeedbackClick = useCallback(() => {
    if (!feedbackTxId) return;
    if (!feedbackText.trim()) { setFeedBackError('Please enter your feedback.'); return; }
    if (feedbackText.length > 250) { setFeedBackError('Please keep feedback within 250 characters.'); return; }
    setFeedBackError('');
    setFeedbackSubmitting(true);
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAICommissionStatement/feedback`,
      { Target: 4, TransactionId: feedbackTxId, IsLiked: feedbackRating, Feedback: feedbackText },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    )
      .then(() => { setFeedbackSubmitting(false); showSuccess('Feedback submitted successfully!'); closeFeedback(); })
      .catch((err: any) => {
        setFeedbackSubmitting(false);
        if (err.response?.status === 401) handleUnauthorized();
        else showError('Error submitting feedback. Please try again.');
      });
  }, [feedbackTxId, feedbackText, feedbackRating, closeFeedback]);

  /* Click-outside to close feedback popup */
  useEffect(() => {
    if (!feedbackTxId) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) closeFeedback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackTxId, closeFeedback]);

  /* Filtered rows */
  const filteredRows = rows.filter(r => {
    if (!globalFilter.trim()) return true;
    const s = globalFilter.toLowerCase();
    return (
      r.fileName?.toLowerCase().includes(s) ||
      r.userName?.toLowerCase().includes(s) ||
      r.status?.toLowerCase().includes(s)
    );
  });

  return (
    <>
      <div className="cs-history-wrap">
        {/* Search + Refresh bar */}
        <div className="cs-history-toolbar">
          <div className="cs-search-box">
            <FiSearch size={14} className="cs-search-box__icon" />
            <input
              className="cs-search-box__input"
              placeholder="Search by file name, user, or status…"
              value={globalFilter}
              onChange={e => setGlobalFilter(e.target.value)}
            />
          </div>
          <button className="cs-icon-btn" title="Refresh" onClick={() => fetchHistory(page)}>
            <FiRefreshCw size={15} className={loading && rows.length > 0 ? 'csh-spin' : ''} />
          </button>
        </div>

        {/* Table */}
        {loading && rows.length === 0 ? (
          <div className="qp-history-loader data-quality-scan-history__loader">
            <div className="qp-history-loader__orb">
              <div className="qp-history-loader__ring qp-history-loader__ring--1" />
              <div className="qp-history-loader__ring qp-history-loader__ring--2" />
              <div className="qp-history-loader__core"><FiClock size={16} /></div>
            </div>
            <div className="qp-history-loader__text">
              <p className="qp-history-loader__title">Loading History</p>
              <p className="qp-history-loader__sub">
                Fetching data quality scan records
                <span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span>
              </p>
            </div>
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="qp-empty-state">
            <FiSearch size={28} />
            <p>{globalFilter ? 'No records match your search.' : 'No history records found.'}</p>
          </div>
        ) : (
          <div className="qp-table-wrap glass-surface">
            <table className="qp-table">
              <thead>
                <tr>
                  <th className="qp-table__th">File Name</th>
                  <th className="qp-table__th">Submitted Date</th>
                  <th className="qp-table__th">User</th>
                  <th className="qp-table__th">Updated in AMS</th>
                  <th className="qp-table__th">Status</th>
                  <th className="qp-table__th data-quality-scan-history__center">Action</th>
                  <th className="qp-table__th data-quality-scan-history__center">Feedback</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row, i) => {
                  const isProcessing = row.status === 'Started' || row.status === 'Inprogress';
                  return (
                    <tr key={row.transactionId ?? i} className="qp-table__row">
                      <td
                        className="qp-table__td data-quality-scan-history__filename-cell"
                      >
                        {row.fileName}
                      </td>
                      <td className="qp-table__td data-quality-scan-history__date-cell">
                        {formatDate(row.created)}
                      </td>
                      <td className="qp-table__td">{row.userName}</td>
                      <td className="qp-table__td">
                        <span
                          className="data-quality-scan-history__ams-flag"
                          style={{ color: row.isUpdateInAMS ? '#10b981' : 'var(--text-tertiary)' }}
                        >
                          {row.isUpdateInAMS ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="qp-table__td">
                        <span
                          className="qp-status-pill"
                          style={{ color: (STATUS_CONFIG[row.status] || STATUS_CONFIG.Failed).color }}
                        >
                          {(STATUS_CONFIG[row.status] || { label: row.status }).label}
                        </span>
                      </td>
                      <td className="qp-table__td">
                        <div className="data-quality-scan-history__action-cell">
                          {isProcessing ? (
                            row.isCheckingStatus
                              ? <div className="dqs-spinner dqs-spinner--sm" />
                              : <button className="cs-action-btn cs-action-btn--sm" onClick={() => checkStatus(row)}>Check Status</button>
                          ) : row.status === 'Success' ? (
                            <button className="qp-table__action-btn" title="Preview results" onClick={() => openPreview(row)}>
                              <FiEye size={16} />
                            </button>
                          ) : (
                            <span className="data-quality-scan-history__not-available">—</span>
                          )}
                        </div>
                      </td>
                      <td className="qp-table__td">
                        <div className="data-quality-scan-history__action-cell">
                          <button
                            className="qp-table__action-btn"
                            title="Feedback"
                            onClick={e => openFeedback(row, e)}
                          >
                            <FiMessageSquare size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalCount > 0 && (
          <div className="qp-pagination">
            <span className="qp-pagination__info">
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
            </span>
            <div className="qp-pagination__controls">
              <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(1)} title="First">«</button>
              <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} title="Previous">‹</button>
              {pageList.map((p, i) =>
                p === '…'
                  ? <span key={`ellipsis-${i}`} className="qp-pagination__ellipsis">…</span>
                  : <button
                      key={p}
                      className={`qp-pagination__btn${page === p ? ' qp-pagination__btn--active' : ''}`}
                      onClick={() => setPage(p as number)}
                    >{p}</button>
              )}
              <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} title="Next">›</button>
              <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Last">»</button>
            </div>
          </div>
        )}

        {/* Results dialog */}
        <Dialog
          header={<span className="data-quality-scan-history__dialog-header">Data Quality Results</span>}
          visible={previewOpen}
          style={{ width: '92vw', maxWidth: 1500 }}
          contentStyle={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '80vh' }}
          onHide={() => setPreviewOpen(false)}
          draggable={false}
          resizable={false}
          modal
          className="cspr-dialog"
        >
          <DataQualityScanResult
            matchesResponseJson={previewData}
            transactionId={previewTxId}
            isUpdateInAMS={previewUpdateFlag}
          />
        </Dialog>
      </div>

      {/* ── Feedback Popup ── */}
      {feedbackTxId && feedbackAnchor && (
        <div
          ref={feedbackRef}
          className="csfb-popup"
          style={{ top: feedbackAnchor.top, right: feedbackAnchor.right }}
        >
          <h4 className="csfb-title">Feedback</h4>
          <textarea
            className="csfb-textarea"
            placeholder="Enter your feedback here"
            value={feedbackText}
            onChange={e => setFeedbackText(e.target.value)}
            rows={4}
          />
          <div className="csfb-rating">
            <button
              className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`}
              onClick={onThumbsUpClick}
              title="Thumbs up"
            >
              <FiThumbsUp size={20} />
            </button>
            <button
              className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`}
              onClick={onThumbsDownClick}
              title="Thumbs down"
            >
              <FiThumbsDown size={20} />
            </button>
          </div>
          {feedBackError && <p className="csfb-error">{feedBackError}</p>}
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
            <button
              className="csfb-btn-submit"
              disabled={feedbackSubmitting}
              onClick={onSubmitFeedbackClick}
            >
              {feedbackSubmitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default DataQualityScanHistory;

